import type { ViewJobReadableCookies } from "../../../shared/native-modules/core/getCookies";

import { enrichJobResultTrackingKey } from "./enrichJobResultTrackingKey";
import { useBrandedJobProctorData } from "./useBrandedJobProctorData";
import { useDualResumesEnabled } from "../../../shared/lib/DualResume/useDualResumesEnabled";
import { deleteCookiesForViewJob } from "../../../shared/native-modules/core/deleteCookies";
import { useTareData } from "../../../shared/native-modules/tare/TareDataContext";
import { CookieKey } from "../../../shared/native-modules/types/RNCoreNativeModule";
import { useCookies } from "../../../shared/util/CookieProvider";
import { isSSR } from "../../../shared/util/isSSR";
import { useViewjobProctorAllocation } from "../../proctor/__generated__/ViewjobProctorContext";
import {
  MobVjApplyRateLimitTst,
  NexusJobSeekerDisclaimer,
  RnJsProMobVjEmpInsightsTst,
  RnJsProVjProInsightsTog,
} from "../../proctor/__generated__/ViewjobProctorSpecifications";
import { useViewjobQuery } from "../__generated__/viewjob.gqlgenerated";

import { useEffect, useMemo } from "react";
import { Platform } from "react-native";

export function useViewJobData(url: string, jobKey: string) {
  const cookies = useCookies<ViewJobReadableCookies>();
  const tareData = useTareData();
  const isLoggedIn = tareData?.accountStatus?.isLoggedIn ?? false;
  const mobVjApplyRateLimitAllocation = useViewjobProctorAllocation(MobVjApplyRateLimitTst);
  const { shouldQueryBrandingEligibility } = useBrandedJobProctorData();
  const isWebSSR = Platform.OS === "web" && isSSR();

  // Delete MCLK cookie after reading (aligns with getVJData native method)
  useEffect(() => {
    void deleteCookiesForViewJob([CookieKey.clickTrackingLog]);
  }, []);

  const cookieModel = useMemo(() => {
    return {
      clickTrackingLog: cookies[CookieKey.clickTrackingLog] ?? undefined,
      previousPageNumber: cookies[CookieKey.previousPageNumber] ?? undefined,
      publisherNumber: cookies[CookieKey.publisherNumber] ?? undefined,
      applyCountHistogram: cookies[CookieKey.applyCountHistogram] ?? undefined,
      jobseekerCookieModel: {
        referral: cookies[CookieKey.referral] ?? undefined,
      },
    };
  }, [cookies]);

  const jobResultTrackingKey = useMemo(() => enrichJobResultTrackingKey(url), [url]);

  const shouldQueryApplyRateLimit = mobVjApplyRateLimitAllocation.value === 1 && isLoggedIn;
  const isJsProVjProInsightsTogActive =
    useViewjobProctorAllocation(RnJsProVjProInsightsTog).value > 0;
  const isJsProMobVjEmpInsightsTstActive =
    useViewjobProctorAllocation(RnJsProMobVjEmpInsightsTst).value > 0;
  const enableEmployerInsights = isJsProMobVjEmpInsightsTstActive && isJsProVjProInsightsTogActive;

  // DualResume M2. The first async dependency of this query: it selects which store the resume
  // comes from, so it has to be known before the request goes out. `cache-and-network` reports
  // loading only when the value is genuinely unknown, so this waits on a cold start and is free
  // once the flag is cached. Skipped when logged out — there is no resume to route, and anonymous
  // traffic must not wait on a per-job-seeker flag before the main query goes out.
  const { isDualResumeObjectsEnabled, loading: dualResumeGateLoading } = useDualResumesEnabled({
    skip: !isLoggedIn,
  });

  const nexusDisclaimerAllocation = useViewjobProctorAllocation(NexusJobSeekerDisclaimer);
  const enableNexusDisclaimer = nexusDisclaimerAllocation.value > 0;
  const enableNexusAccommodation = nexusDisclaimerAllocation.isShowaccommodation;

  const variables = useMemo(
    () => ({
      input: jobKey,
      detailsInput: {
        viewjobUrl: url,
        viewjobCookieModel: cookieModel,
        shouldQueryApplyRateLimit,
      },
      jobResultTrackingKey,
      isLoggedIn,
      enableEmployerInsights,
      shouldQueryBrandingEligibility,
      enableNexusDisclaimer,
      enableNexusAccommodation,
      // Gates every field sourced from the RNW SSR ViewJob OneGraph experiment.
      includeWebSsrOnegraphFields: isWebSSR,
      // Matches the `isLoggedIn` gate on the `jobSeekerProfile` selection: neither store is
      // read for an anonymous seeker.
      useResumeCollection: isLoggedIn && isDualResumeObjectsEnabled,
    }),
    [
      cookieModel,
      jobKey,
      url,
      jobResultTrackingKey,
      isLoggedIn,
      shouldQueryApplyRateLimit,
      enableEmployerInsights,
      shouldQueryBrandingEligibility,
      enableNexusDisclaimer,
      enableNexusAccommodation,
      isWebSSR,
      isDualResumeObjectsEnabled,
    ],
  );

  const result = useViewjobQuery({
    variables,
    skip: dualResumeGateLoading,
    // Allow Apollo to return partial data for non-critical GraphQL field errors.
    errorPolicy: "all",
  });

  // Apollo reports `loading: false` while skipped, which callers would read as "settled with no
  // data" — `useViewJobInitialData` maps that to an idle status rather than an in-flight one.
  // The gate wait is part of this query's load.
  return { ...result, loading: dualResumeGateLoading || result.loading };
}
